import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlideOut, Badge, Avatar, Button, Input, TextArea, HighlightCard, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Eye, Pencil, Webhook, X } from 'lucide-react';
import {
  getRequest,
  rejectRequest,
  markRequestInProgress,
  markRequestDelivered,
  sendRequestQuote,
  notifyRequestEvent,
} from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { REQUEST_STATUS_CHIP, requestStatusLabelKey } from '../../constants';
import {
  initialsOf,
  REQUESTER_AVATAR_CLASS,
  formatRelativeTime,
  CHIP_BASE,
  CHIP_LABEL,
} from '../../utils';

const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-content-faint';
const META_LABEL = 'text-[10px] font-medium uppercase tracking-[0.06em] text-content-faint';
const META_VALUE = 'mt-0.5 text-[13px] font-medium text-content';
const FOOTER_BTN = 'min-w-0 flex-1';
const DECLINE_CONFIRM_BTN = [
  FOOTER_BTN,
  '!border-[rgba(239,68,68,0.35)] !bg-[rgba(127,29,29,0.28)] !text-[#F87171]',
  'hover:!bg-[rgba(127,29,29,0.42)]',
].join(' ');
const STATUS_CHIP = `${CHIP_BASE} !h-auto !min-h-5 !max-h-none !gap-1.5 !rounded-[8px] !py-0.5`;

const ACCESS_ICON = { read: Eye, write: Pencil, push: Webhook };
const SENSITIVITY_KEYS = ['pii', 'financial', 'compliance'];
const BUILD_STATUSES = ['approved', 'in_progress'];

const formatActivityTimestamp = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Uppercase section header with a trailing hairline rule (governance drawer pattern). */
const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2">
    <span className={SECTION_LABEL}>{children}</span>
    <span className="h-px flex-1 bg-edge dark:bg-white/[0.06]" aria-hidden="true" />
  </div>
);

/** Label + value cell for the Request details grid. */
const MetaCell = ({ label, value }) => (
  <div className="min-w-0">
    <p className={META_LABEL}>{label}</p>
    <p className={META_VALUE}>{value}</p>
  </div>
);

/**
 * Request Detail Panel (CT-4201). Right `SlideOut` an admin opens from an inbox
 * row (CT-4200) to review a request and drive its lifecycle. Footer actions stay
 * pinned; Decline / Send quote expand their form above the action row. Approve
 * starts the build (→ in_progress) and swaps the footer to Open build / Mark delivered.
 */
const RequestDetailPanel = ({ ticket, onClose, onChanged, readOnly = false }) => {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [mode, setMode] = useState('idle'); // 'idle' | 'decline' | 'quote'
  const [declineReason, setDeclineReason] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteWeeks, setQuoteWeeks] = useState('');
  const [quoteNote, setQuoteNote] = useState('');
  const submittingRef = useRef(false);
  const fetchIdRef = useRef(0);

  const fetchRequest = useCallback(() => {
    if (!ticket) return Promise.resolve();
    const id = ++fetchIdRef.current;
    return getRequest(ticket)
      .then((res) => {
        // Discard a stale response if the ticket changed while it was in flight.
        if (id === fetchIdRef.current) setRequest(validateApiResponse(res, t('common.requestFailed')).body);
      })
      .catch(() => {
        if (id === fetchIdRef.current) setRequest(null);
      });
  }, [ticket, t]);

  useEffect(() => {
    fetchRequest();
    return () => {
      setMode('idle');
      setDeclineReason('');
      setQuoteAmount('');
      setQuoteWeeks('');
      setQuoteNote('');
    };
  }, [fetchRequest]);

  const run = useCallback(
    async (fn, { close = false } = {}) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        await fn();
        onChanged?.();
        if (close) {
          onClose();
          return;
        }
        await fetchRequest();
        setMode('idle');
      } catch (err) {
        toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
      } finally {
        submittingRef.current = false;
      }
    },
    [fetchRequest, onChanged, onClose, t, toast],
  );

  // CT-4204 — request status notifications (trigger wiring).
  //
  // Each lifecycle action fires a notification to the requester and shows the
  // acting admin a local `useToast` confirmation. Delivery to the requester is
  // PLATFORM-level (the AIMS-OS notification bell/panel via @aimsos/websocket)
  // and owned outside this domain; in V1 `notifyRequestEvent` is a stub that
  // just returns the payload. TODO(CT-3904 / backend + platform notifications).
  //
  // These run best-effort and are intentionally NOT awaited inside `run`'s
  // critical path: the mutation (reject / in-progress) owns the success flow
  // (`onChanged` + refetch). The notification is a secondary side-effect, so a
  // failure only skips the confirmation toast — it never rolls back or masks a
  // successful action. This is the seam that stays correct once the real
  // platform channel (which can reject at runtime) replaces the stub.
  const notifyRejected = useCallback(
    (ticketNumber, reason) => {
      notifyRequestEvent('rejected', ticketNumber, { reason })
        .then(() => toast.success({ message: t('integrations.requests.notify.rejected'), canClose: true }))
        .catch(() => {});
    },
    [t, toast],
  );

  const notifyInProgress = useCallback(
    (ticketNumber) => {
      notifyRequestEvent('in_progress', ticketNumber)
        .then(() => toast.success({ message: t('integrations.requests.notify.inProgress'), canClose: true }))
        .catch(() => {});
    },
    [t, toast],
  );

  const current = ticket && request?.ticket_number === ticket ? request : null;
  const isOpen = Boolean(current);
  const status = current?.status;

  const activity = useMemo(() => {
    if (!current) return [];
    const items = [
      {
        id: 'created',
        title: t('integrations.requests.detail.requestedActivity', { name: current.requester }),
        description: formatActivityTimestamp(current.date_created),
      },
    ];
    if (current.quote_usd != null) {
      items.push({
        id: 'quoted',
        title: t('integrations.requests.detail.milestone.quotedTitle', {
          amount: Number(current.quote_usd).toLocaleString(),
        }),
        description: t('integrations.requests.detail.milestone.quotedDesc', {
          count: current.eta_weeks ?? 0,
        }),
      });
    }
    if (BUILD_STATUSES.includes(status)) {
      items.push({
        id: 'build',
        title: t('integrations.requests.detail.milestone.buildTitle'),
        description: t('integrations.requests.detail.milestone.buildDesc'),
      });
    }
    (current.comments ?? []).forEach((c) => {
      items.push({ id: c.id, title: `${c.author}: ${c.body}`, description: '' });
    });
    return items;
  }, [current, status, t]);

  const isHighPriority = current?.priority === 'high' || current?.priority === 'critical';
  const statusChip = REQUEST_STATUS_CHIP[status] ?? REQUEST_STATUS_CHIP.pending;

  const renderFooter = () => {
    if (readOnly || !current) return null;

    if (mode === 'decline') {
      return (
        <div className="flex flex-col gap-3">
          <TextArea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder={t('integrations.requests.detail.declineReason')}
            rows={3}
            className="!resize-y"
          />
          <div className="flex gap-2">
            <Button variant="secondary" size="md" className={FOOTER_BTN} onClick={() => setMode('idle')}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              className={DECLINE_CONFIRM_BTN}
              disabled={!declineReason.trim()}
              onClick={() =>
                run(async () => {
                  await rejectRequest(ticket, declineReason.trim());
                  // CT-4204: notify the requester their request was declined.
                  // Best-effort — the mutation above owns the success path; a
                  // notification failure must not undo it (see notifyRejected).
                  notifyRejected(ticket, declineReason.trim());
                })
              }
            >
              {t('integrations.requests.detail.declineConfirm')}
            </Button>
          </div>
        </div>
      );
    }

    if (mode === 'quote') {
      return (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              min="0"
              value={quoteAmount}
              onChange={(e) => setQuoteAmount(e.target.value)}
              placeholder={t('integrations.requests.detail.quoteAmount')}
              aria-label={t('integrations.requests.detail.quoteAmount')}
            />
            <Input
              type="number"
              min="0"
              value={quoteWeeks}
              onChange={(e) => setQuoteWeeks(e.target.value)}
              placeholder={t('integrations.requests.detail.quoteWeeks')}
              aria-label={t('integrations.requests.detail.quoteWeeks')}
            />
          </div>
          <TextArea
            value={quoteNote}
            onChange={(e) => setQuoteNote(e.target.value)}
            placeholder={t('integrations.requests.detail.quoteScope')}
            rows={3}
            className="!resize-y"
          />
          <div className="flex gap-2">
            <Button variant="secondary" size="md" className={FOOTER_BTN} onClick={() => setMode('idle')}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="classicPrimary"
              size="md"
              className={FOOTER_BTN}
              disabled={!quoteAmount.trim() || !quoteWeeks.trim()}
              onClick={() =>
                run(() =>
                  sendRequestQuote(ticket, {
                    amount: Number(quoteAmount),
                    weeks: Number(quoteWeeks),
                    note: quoteNote.trim() || null,
                  }),
                )
              }
            >
              {t('integrations.requests.detail.sendQuote')}
            </Button>
          </div>
        </div>
      );
    }

    if (status === 'delivered') {
      return (
        <Button
          variant="classicPrimary"
          size="md"
          className="w-full"
          onClick={() => {
            toast.info({
              message: t('integrations.requests.detail.openConnectionSoon'),
              canClose: true,
            });
            onClose();
          }}
        >
          {t('integrations.requests.detail.openConnection')}
        </Button>
      );
    }

    if (BUILD_STATUSES.includes(status)) {
      return (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="md"
            className={FOOTER_BTN}
            onClick={() => {
              toast.info({
                message: t('integrations.requests.detail.openBuildSoon'),
                canClose: true,
              });
              onClose();
            }}
          >
            {t('integrations.requests.detail.openBuild')}
          </Button>
          <Button
            variant="classicPrimary"
            size="md"
            className={FOOTER_BTN}
            onClick={() =>
              run(async () => {
                await markRequestDelivered(ticket);
                toast.success({
                  message: t('integrations.requests.detail.markedDelivered'),
                  canClose: true,
                });
              })
            }
          >
            {t('integrations.requests.detail.markDelivered')}
          </Button>
        </div>
      );
    }

    if (status === 'pending' || status === 'quoted') {
      return (
        <div className="flex gap-2">
          <Button variant="secondary" size="md" className={FOOTER_BTN} onClick={() => setMode('decline')}>
            {t('integrations.requests.detail.decline')}
          </Button>
          {status === 'pending' ? (
            <Button variant="secondary" size="md" className={FOOTER_BTN} onClick={() => setMode('quote')}>
              {t('integrations.requests.detail.sendQuote')}
            </Button>
          ) : null}
          <Button
            variant="classicPrimary"
            size="md"
            className={FOOTER_BTN}
            onClick={() =>
              run(async () => {
                await markRequestInProgress(ticket);
                // CT-4204: notify the requester the build is underway (approve → in progress).
                // Best-effort — see notifyInProgress; the mutation owns success.
                notifyInProgress(ticket);
              })
            }
          >
            {status === 'quoted'
              ? t('integrations.requests.detail.approveQuoteAndBuild')
              : t('integrations.requests.detail.approve')}
          </Button>
        </div>
      );
    }

    return null;
  };

  const footer = renderFooter();

  return (
    <SlideOut
      isOpen={isOpen}
      onClose={onClose}
      size="M"
      renderHeader={(titleId) =>
        current ? (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Badge variant="neutral" size="small" className={`${STATUS_CHIP} ${statusChip.className}`}>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusChip.dot}`} aria-hidden="true" />
                  <span className={CHIP_LABEL}>{t(requestStatusLabelKey(status))}</span>
                </Badge>
                {isHighPriority ? (
                  <span className="inline-flex items-center rounded-[8px] border border-[rgba(239,68,68,0.45)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#EF4444]">
                    {t('integrations.requests.detail.priorityHigh')}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.cancel')}
                className="inline-flex size-7 shrink-0 items-center justify-center rounded p-1 text-[#52525B] transition-colors hover:bg-black/5 dark:text-[#D1D5DB] dark:hover:bg-white/10"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div>
              <h2
                id={titleId}
                className="text-[24px] font-semibold leading-[29px] tracking-[0.25px] text-content"
              >
                {current.name}
              </h2>
              <p className="mt-0.5 text-[14px] font-medium leading-5 text-content-secondary">
                {t('integrations.requests.detail.subtitle', {
                  vendor: current.vendor ?? current.name,
                  connector: t(`integrations.requests.detail.connector.${current.connector_type ?? 'official'}`),
                })}
              </p>
            </div>
          </div>
        ) : null
      }
    >
      {current && (
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto scrollbar-aimsos">
            <section className="flex flex-col gap-2">
              <SectionLabel>{t('integrations.requests.detail.requestedBy')}</SectionLabel>
              <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.04] p-3 ring-1 ring-inset ring-white/[0.06]">
                <Avatar alt={current.requester} initials={initialsOf(current.requester)} className={REQUESTER_AVATAR_CLASS} />
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold text-content">{current.requester}</p>
                  <p className="truncate text-[12px] text-content-faint">
                    {[
                      current.requester_role,
                      current.workspace,
                      current.department && t(`integrations.request.departments.${current.department}`),
                      formatRelativeTime(current.date_created, t),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </div>
            </section>

            {current.business_intent ? (
              <section className="flex flex-col gap-2">
                <SectionLabel>{t('integrations.requests.detail.businessCase')}</SectionLabel>
                <p className="text-[13px] leading-relaxed text-content">{current.business_intent}</p>
              </section>
            ) : null}

            <section className="flex flex-col gap-2.5">
              <SectionLabel>{t('integrations.requests.detail.details')}</SectionLabel>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <MetaCell
                  label={t('integrations.requests.detail.scope')}
                  value={[current.workspace, current.department && t(`integrations.request.departments.${current.department}`)]
                    .filter(Boolean)
                    .join(' · ')}
                />
                {current.expected_users != null ? (
                  <MetaCell
                    label={t('integrations.requests.detail.peopleLabel')}
                    value={t('integrations.requests.detail.people', { count: current.expected_users })}
                  />
                ) : null}
                {current.timeline_weeks != null ? (
                  <MetaCell
                    label={t('integrations.requests.detail.timelineLabel')}
                    value={t('integrations.requests.detail.weeks', { count: current.timeline_weeks })}
                  />
                ) : null}
                <MetaCell
                  label={t('integrations.requests.detail.connectorLabel')}
                  value={t(`integrations.requests.detail.connector.${current.connector_type ?? 'official'}`)}
                />
              </div>
            </section>

            {(current.action_types ?? []).length > 0 ? (
              <section className="flex flex-col gap-2">
                <SectionLabel>{t('integrations.requests.detail.accessRequested')}</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {current.action_types.map((a) => {
                    const Icon = ACCESS_ICON[a];
                    return (
                      <Badge key={a} variant="neutral" size="small" icon={Icon}>
                        {t(`integrations.request.actionTypes.${a}`)}
                      </Badge>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <SectionLabel>{t('integrations.requests.detail.dataSensitivity')}</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {SENSITIVITY_KEYS.filter((k) => current.data_sensitivity?.[k] === 'yes').map((k) => (
                  <Badge key={k} variant="warning" size="small">
                    {t(`integrations.requests.detail.sensitivity.${k}`)}
                  </Badge>
                ))}
                {SENSITIVITY_KEYS.every((k) => current.data_sensitivity?.[k] !== 'yes') ? (
                  <span className="text-[13px] text-content-faint">
                    {t('integrations.requests.detail.sensitivity.none')}
                  </span>
                ) : null}
              </div>
            </section>

            {current.quote_usd != null && status !== 'delivered' ? (
              <section className="flex flex-col gap-2">
                <SectionLabel>{t('integrations.requests.detail.quoteSection')}</SectionLabel>
                <HighlightCard
                  variant="neutral"
                  showIcon={false}
                  value={`$${Number(current.quote_usd).toLocaleString()}`}
                  unit={t('integrations.requests.detail.quoteEta', { count: current.eta_weeks ?? 0 })}
                  feedback={current.quote_note || undefined}
                  showFeedback={Boolean(current.quote_note)}
                  unitClassName="ml-auto !text-[#C4B5FD]"
                />
              </section>
            ) : null}

            <section className="flex flex-col gap-2.5 pb-1">
              <SectionLabel>{t('integrations.requests.detail.activity')}</SectionLabel>
              <ul className="relative flex flex-col gap-4">
                {activity.length > 1 ? (
                  <span
                    className="absolute bottom-2 left-[3px] top-2 w-px bg-white/[0.08]"
                    aria-hidden="true"
                  />
                ) : null}
                {activity.map((item) => (
                  <li key={item.id} className="relative flex gap-2.5">
                    <span className="relative z-10 mt-1.5 size-2 shrink-0 rounded-full bg-[#80AFFF]" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-[13px] text-content">{item.title}</p>
                      {item.description ? (
                        <p className="text-[11px] text-content-faint">{item.description}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {footer ? (
            <div className="shrink-0 border-t border-edge pt-3">{footer}</div>
          ) : null}
        </div>
      )}
    </SlideOut>
  );
};

export default RequestDetailPanel;
